/**
 * useSearch Hook - React hook for search functionality
 * TASK-270: Unified Search Interface
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { searchService } from '../services/searchService';
import type {
  SearchRequest,
  SearchResponse,
  SearchStatus,
  SourceAttribution,
  SourceType,
} from '../types/search.types';

export interface UseSearchOptions {
  streaming?: boolean;
  onSearchStart?: () => void;
  onSearchComplete?: (response: SearchResponse) => void;
  onSearchError?: (error: Error) => void;
}

export interface UseSearchResult {
  // State
  isSearching: boolean;
  response: SearchResponse | null;
  error: Error | null;
  status: SearchStatus | null;

  // Streaming state
  partialAnswer: string;
  streamingSources: SourceAttribution[];

  // Actions
  search: (query: string, sources?: SourceType[]) => Promise<void>;
  searchStreaming: (query: string, sources?: SourceType[]) => void;
  reset: () => void;
  cancelSearch: () => void;
}

export function useSearch(options: UseSearchOptions = {}): UseSearchResult {
  const { onSearchStart, onSearchComplete, onSearchError } = options;

  // State
  const [isSearching, setIsSearching] = useState(false);
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<SearchStatus | null>(null);

  // Streaming state
  const [partialAnswer, setPartialAnswer] = useState('');
  const [streamingSources, setStreamingSources] = useState<SourceAttribution[]>([]);

  // Refs
  const cleanupRef = useRef<(() => void) | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      abortControllerRef.current?.abort();
    };
  }, []);

  // Reset state
  const reset = useCallback(() => {
    setIsSearching(false);
    setResponse(null);
    setError(null);
    setStatus(null);
    setPartialAnswer('');
    setStreamingSources([]);
  }, []);

  // Cancel search
  const cancelSearch = useCallback(() => {
    cleanupRef.current?.();
    abortControllerRef.current?.abort();
    setIsSearching(false);
    setStatus(null);
  }, []);

  // Non-streaming search with abort support
  const search = useCallback(
    async (query: string, sources?: SourceType[]) => {
      if (!query.trim()) {
        setError(new Error('Query cannot be empty'));
        return;
      }

      // Cancel any pending request before starting new one
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      reset();
      setIsSearching(true);
      setStatus('in_progress');
      onSearchStart?.();

      try {
        const request: SearchRequest = {
          query,
          sources,
          streaming: false,
        };

        // Pass abort signal to search service
        const result = await searchService.search(
          request,
          abortControllerRef.current.signal
        );

        // Only update state if not aborted
        if (!abortControllerRef.current.signal.aborted) {
          setResponse(result);
          setStatus(result.status);
          onSearchComplete?.(result);
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        const error = err instanceof Error ? err : new Error('Search failed');
        setError(error);
        setStatus('failed');
        onSearchError?.(error);
      } finally {
        setIsSearching(false);
      }
    },
    [reset, onSearchStart, onSearchComplete, onSearchError]
  );

  // Streaming search
  // TASK-296: Fixed stale closure bug by using local accumulation variables
  const searchStreaming = useCallback(
    (query: string, sources?: SourceType[]) => {
      if (!query.trim()) {
        setError(new Error('Query cannot be empty'));
        return;
      }

      reset();
      setIsSearching(true);
      setStatus('in_progress');
      onSearchStart?.();

      // Local accumulation variables to avoid stale closure issue
      // React state updates are async, so callbacks would capture stale values
      let accumulatedAnswer = '';
      let accumulatedSources: SourceAttribution[] = [];

      const request: SearchRequest = {
        query,
        sources,
        streaming: true,
      };

      cleanupRef.current = searchService.searchStream(request, {
        onToken: (content) => {
          accumulatedAnswer += content;
          setPartialAnswer(accumulatedAnswer);
        },
        onSource: (source) => {
          accumulatedSources = [...accumulatedSources, source];
          setStreamingSources(accumulatedSources);
        },
        onStatus: (newStatus) => {
          setStatus(newStatus);
        },
        onDone: (metadata) => {
          setIsSearching(false);
          setStatus('completed');

          // Build final response from local accumulated data (not stale closure)
          const finalResponse: SearchResponse = {
            answer: accumulatedAnswer,
            status: 'completed',
            insights: [],  // Streaming doesn't provide insights; use non-streaming for full insights
            sources: accumulatedSources,
            toolResults: [],
            query,
            intent: 'unknown',
            confidence: metadata.confidence as number || 0,
            queryTimeMs: metadata.query_time_ms as number || 0,
            failedSources: [],
            warnings: [],
            followUpSuggestions: metadata.follow_up_suggestions as string[] || [],
          };

          setResponse(finalResponse);
          onSearchComplete?.(finalResponse);
        },
        onError: (errorMsg) => {
          const error = new Error(errorMsg);
          setError(error);
          setStatus('failed');
          setIsSearching(false);
          onSearchError?.(error);
        },
      });
    },
    [reset, onSearchStart, onSearchComplete, onSearchError]
  );

  return {
    // State
    isSearching,
    response,
    error,
    status,

    // Streaming state
    partialAnswer,
    streamingSources,

    // Actions
    search,
    searchStreaming,
    reset,
    cancelSearch,
  };
}

export default useSearch;
